import { adminFetch } from '../../lib/adminFetch';
import React, { useState, useEffect } from 'react';

const eventTypes = [
  { id: 'study1', label: 'アカデミー勉強会 1回目', color: 'bg-yellow-100 text-yellow-800' },
  { id: 'study2', label: 'アカデミー勉強会 2回目', color: 'bg-orange-100 text-orange-800' },
  { id: 'study3', label: 'アカデミー勉強会 3回目', color: 'bg-red-100 text-red-800' },
  { id: 'lab', label: '実践スキルアップLAB', color: 'bg-purple-100 text-purple-800' },
  { id: 'video', label: '動画コンテンツ', color: 'bg-green-100 text-green-800' },
];

const getEventColor = (type) => eventTypes.find((t) => t.id === type)?.color || 'bg-gray-100 text-gray-800';
const getEventLabel = (type) => eventTypes.find((t) => t.id === type)?.label || type;

const dayNames = ['日', '月', '火', '水', '木', '金', '土'];
const formatEventDate = (dateStr) => {
  if (!dateStr || dateStr === '不定期配信') return dateStr;
  // 既に「4/6（月）」形式ならそのまま
  if (dateStr.includes('（') || dateStr.includes('/')) return dateStr;
  // 2026-04-06 形式 → 4/6（月）
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return `${d.getMonth() + 1}/${d.getDate()}（${dayNames[d.getDay()]}）`;
};

const ScheduleAdmin = () => {
  const [schedules, setSchedules] = useState([]);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newStartMonth, setNewStartMonth] = useState('');

  // イベント追加用
  const [addMonthIndex, setAddMonthIndex] = useState(null);
  const [addEventType, setAddEventType] = useState('lab');
  const [addEventDate, setAddEventDate] = useState('');
  const [addEventTime, setAddEventTime] = useState('');

  useEffect(() => {
    adminFetch('/api/admin-read.php?file=academy-schedules&t=' + Date.now())
      .then((r) => r.ok ? r.json() : [])
      .then(setSchedules)
      .catch(() => setSchedules([]));
  }, []);

  const saveSchedules = async (data) => {
    setSaving(true);
    try {
      const res = await adminFetch('/api/admin-data.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: 'academy-schedules', data }),
      });
      const result = await res.json();
      if (result.success) {
        setSchedules(data);
        setMessage('保存しました');
        setTimeout(() => setMessage(''), 2000);
      }
    } catch {}
    setSaving(false);
  };

  const createSchedule = () => {
    if (!newStartMonth.trim()) return;
    const months = [];
    const startNum = parseInt(newStartMonth);
    for (let i = 0; i < 6; i++) {
      const m = ((startNum - 1 + i) % 12) + 1;
      const events = [];
      // 1,3,6ヶ月目に勉強会
      if (i === 0) events.push({ type: 'study1', date: '', time: '19:00〜' });
      if (i === 2) events.push({ type: 'study2', date: '', time: '19:00〜' });
      if (i === 5) events.push({ type: 'study3', date: '', time: '19:00〜' });
      events.push({ type: 'lab', date: '', time: '17:30〜18:30' });
      events.push({ type: 'video', date: '不定期配信', time: '' });
      months.push({ monthNum: i + 1, calMonth: m + '月', events });
    }
    const newSchedule = {
      id: String(Date.now()),
      startMonth: newStartMonth + '月スタート',
      months,
    };
    const updated = [...schedules, newSchedule];
    saveSchedules(updated);
    setNewStartMonth('');
    setShowNewForm(false);
    setSelected(newSchedule);
    setMessage(`「${newSchedule.startMonth}」を作成しました`);
  };

  const isScheduleExpired = (schedule) => {
    const firstMonth = schedule.months?.[0];
    if (!firstMonth) return false;
    const firstEvent = firstMonth.events?.find((ev) => ev.date && ev.date !== '不定期配信');
    if (!firstEvent) return false;
    const parts = firstEvent.date.split('-');
    if (parts.length !== 3) return false;
    const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    if (isNaN(d)) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d <= today;
  };

  const deleteSchedule = (id) => {
    if (!window.confirm('このスケジュールを削除しますか？')) return;
    const updated = schedules.filter((s) => s.id !== id);
    saveSchedules(updated);
    if (selected?.id === id) setSelected(null);
  };

  const updateEvent = (scheduleId, monthIndex, eventIndex, field, value) => {
    const updated = schedules.map((s) => {
      if (s.id !== scheduleId) return s;
      const months = [...s.months];
      const events = [...months[monthIndex].events];
      events[eventIndex] = { ...events[eventIndex], [field]: value };
      months[monthIndex] = { ...months[monthIndex], events };
      return { ...s, months };
    });
    setSchedules(updated);
    setSelected(updated.find((s) => s.id === scheduleId));
  };

  const removeEvent = (scheduleId, monthIndex, eventIndex) => {
    const updated = schedules.map((s) => {
      if (s.id !== scheduleId) return s;
      const months = [...s.months];
      const events = months[monthIndex].events.filter((_, i) => i !== eventIndex);
      months[monthIndex] = { ...months[monthIndex], events };
      return { ...s, months };
    });
    setSchedules(updated);
    setSelected(updated.find((s) => s.id === scheduleId));
  };

  const addEvent = (scheduleId, monthIndex) => {
    if (!addEventDate && addEventType !== 'video') return;
    const updated = schedules.map((s) => {
      if (s.id !== scheduleId) return s;
      const months = [...s.months];
      const events = [...months[monthIndex].events, {
        type: addEventType,
        date: addEventType === 'video' ? '不定期配信' : addEventDate,
        time: addEventTime,
      }];
      months[monthIndex] = { ...months[monthIndex], events };
      return { ...s, months };
    });
    setSchedules(updated);
    setSelected(updated.find((s) => s.id === scheduleId));
    setAddMonthIndex(null);
    setAddEventDate('');
    setAddEventTime('');
  };

  const saveAll = () => saveSchedules(schedules);

  // HTMLプレビュー生成
  const generatePreview = () => {
    if (!selected) return;
    const url = `/data/academy-schedules.json`;
    window.open(`/schedule-preview.html?t=${Date.now()}`, '_blank');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">スケジュール管理</h2>
        <div className="flex gap-2">
          <button onClick={() => setShowNewForm(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700">+ 新規スケジュール</button>
          {selected && <button onClick={saveAll} disabled={saving} className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-green-700 disabled:opacity-50">{saving ? '保存中...' : '保存'}</button>}
        </div>
      </div>

      {message && <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">{message}</div>}

      {showNewForm && (
        <div className="bg-white rounded-xl shadow p-4 mb-4 flex items-center gap-3">
          <select value={newStartMonth} onChange={(e) => setNewStartMonth(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">開始月を選択</option>
            {[...Array(12)].map((_, i) => <option key={i} value={i + 1}>{i + 1}月</option>)}
          </select>
          <button onClick={createSchedule} disabled={!newStartMonth} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">作成</button>
          <button onClick={() => setShowNewForm(false)} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">取消</button>
        </div>
      )}

      {/* 凡例 */}
      <div className="flex flex-wrap gap-2 mb-4">
        {eventTypes.map((t) => (
          <span key={t.id} className={`text-[10px] font-bold px-2 py-1 rounded ${t.color}`}>{t.label}</span>
        ))}
      </div>

      <div className="flex gap-6">
        {/* 左：スケジュール一覧 */}
        <div className="w-1/4">
          {schedules.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-6 text-center text-gray-500 text-sm">スケジュールがありません</div>
          ) : (
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="divide-y">
                {schedules.map((s) => (
                  <div key={s.id} onClick={() => setSelected(s)}
                    className={`px-4 py-3 cursor-pointer hover:bg-gray-50 ${selected?.id === s.id ? 'bg-blue-50' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-sm">{s.startMonth}</p>
                        {isScheduleExpired(s) && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">受付終了</span>}
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); deleteSchedule(s.id); }}
                        className="bg-red-50 text-red-600 px-2 py-0.5 rounded text-[10px] font-bold hover:bg-red-100">削除</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 右：スケジュール詳細編集 */}
        <div className="flex-1">
          {selected ? (
            <div className="space-y-3">
              {selected.months.map((month, mi) => (
                <div key={mi} className="bg-white rounded-xl shadow overflow-hidden">
                  <div className="px-4 py-2 bg-gray-50 border-b flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-sm text-purple-700">{month.monthNum}ヶ月目</span>
                      <span className="text-xs text-gray-500">{month.calMonth}</span>
                    </div>
                    <button onClick={() => setAddMonthIndex(addMonthIndex === mi ? null : mi)}
                      className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-[10px] font-bold hover:bg-blue-200">+ 追加</button>
                  </div>

                  {/* イベント一覧 */}
                  <div className="divide-y">
                    {month.events.map((ev, ei) => (
                      <div key={ei} className="px-4 py-2 flex items-center gap-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded shrink-0 ${getEventColor(ev.type)}`}>{getEventLabel(ev.type)}</span>
                        {ev.type === 'video' ? (
                          <span className="flex-1 text-xs text-gray-500">不定期配信</span>
                        ) : (
                          <input type="date" value={ev.date} onChange={(e) => updateEvent(selected.id, mi, ei, 'date', e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        )}
                        <input type="text" value={ev.time} onChange={(e) => updateEvent(selected.id, mi, ei, 'time', e.target.value)}
                          placeholder="時間" className="w-32 border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <button onClick={() => removeEvent(selected.id, mi, ei)}
                          className="bg-red-50 text-red-600 px-2 py-0.5 rounded text-[10px] font-bold hover:bg-red-100">削除</button>
                      </div>
                    ))}
                  </div>

                  {/* イベント追加フォーム */}
                  {addMonthIndex === mi && (
                    <div className="px-4 py-2 bg-blue-50 border-t flex items-center gap-2">
                      <select value={addEventType} onChange={(e) => setAddEventType(e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500">
                        {eventTypes.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                      </select>
                      <input type="date" value={addEventDate} onChange={(e) => setAddEventDate(e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <input type="text" value={addEventTime} onChange={(e) => setAddEventTime(e.target.value)}
                        placeholder="時間" className="w-32 border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <button onClick={() => addEvent(selected.id, mi)}
                        className="bg-blue-600 text-white px-3 py-1 rounded text-xs font-bold hover:bg-blue-700">追加</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">左からスケジュールを選択してください</div>
          )}
        </div>
      </div>

      {/* 備考 */}
      <div className="mt-6 bg-white rounded-xl shadow p-4 text-xs text-gray-600 space-y-1">
        <p>📌 アカデミー勉強会は6ヶ月間で計3回（1・3・6ヶ月目）開催されます。</p>
        <p>📌 実践スキルアップLABは毎月開催されます。</p>
        <p>📌 アカデミー勉強会・LABはアーカイブ動画でもご覧いただけます。</p>
        <p>📌 7ヶ月目以降も月額19,800円（税抜）で、SrWithシステムの利用および実践スキルアップLABに引き続きご参加いただけます。</p>
      </div>
    </div>
  );
};

export default ScheduleAdmin;
